import { lazy, Suspense } from 'react';
import { HashRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './auth/auth-context';
import { Spinner } from './components/shared/Spinner';
import { BackgroundGrid } from './components/layout/BackgroundGrid';
import { DecayParticles } from './components/layout/DecayParticles';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { HomePage } from './pages/HomePage';
import { ForumPage } from './pages/ForumPage';
import { ForumTopicPage } from './pages/ForumTopicPage';
import { AutonomyPage } from './pages/AutonomyPage';
import { SessionReplayPage } from './pages/SessionReplayPage';
import { PoolsPage } from './pages/PoolsPage';
import { StatusPage } from './pages/StatusPage';
import { ProtocolPage } from './pages/ProtocolPage';
import { DocsPage } from './pages/DocsPage';
import { DashboardPage } from './pages/DashboardPage';
import { EntityProfilePage } from './pages/EntityProfilePage';
import { WagerDetailPage } from './pages/WagerDetailPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { TermsPage } from './pages/TermsPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { OAuthSignupPage } from './pages/OAuthSignupPage';

const GivExplainerPage = __FEATURE_GIV_EXPLAINER__
  ? lazy(() => import('./pages/GivExplainerPage').then(m => ({ default: m.GivExplainerPage })))
  : null;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

const AppLayout = (): React.ReactElement => (
  <>
    <BackgroundGrid />
    <DecayParticles />
    <Header />
    <main style={{ flex: 1, padding: '1.5rem', maxWidth: '1100px', margin: '0 auto', width: '100%' }}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/forum" element={<ForumPage />} />
        <Route path="/forum/:topicId" element={<ForumTopicPage />} />
        <Route path="/autonomy" element={<AutonomyPage />} />
        <Route path="/autonomy/:scheduleId/runs/:runId" element={<SessionReplayPage />} />
        <Route path="/pools" element={<PoolsPage />} />
        <Route path="/status" element={<StatusPage />} />
        <Route path="/protocol" element={<ProtocolPage />} />
        <Route path="/mcp" element={<ProtocolPage />} />
        <Route path="/docs" element={<DocsPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/entity/:entityId" element={<EntityProfilePage />} />
        <Route path="/wager/:wagerId" element={<WagerDetailPage />} />
        <Route path="/project/:projectId" element={<ProjectDetailPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/oauth/signup" element={<OAuthSignupPage />} />
        {GivExplainerPage !== null && (
          <Route path="/docs/measuring-the-world" element={
            <Suspense fallback={<Spinner />}>
              <GivExplainerPage />
            </Suspense>
          } />
        )}
      </Routes>
    </main>
    <Footer />
  </>
);

export const App = (): React.ReactElement => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <HashRouter>
        <AppLayout />
      </HashRouter>
    </AuthProvider>
  </QueryClientProvider>
);
